import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import axios from 'axios';
import API_URL from '../config';
import { parseApiError } from '../utils/apiErrors';

const AuthContext = createContext(null);

const buildClientError = (error, fallback) => {
  const clientError = new Error(parseApiError(error, fallback));
  clientError.response = error?.response;
  clientError.code = error?.code;
  clientError.isAxiosError = error?.isAxiosError;
  return clientError;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/auth/me`, {
        withCredentials: true
      });
      setUser(response.data);
    } catch (error) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const response = await axios.post(`${API_URL}/api/auth/login`, 
      { email, password },
      { withCredentials: true }
    );
    setUser(response.data);
    return response.data;
  };

  const register = async (email, password, name, phone, role, vehicleModel = '', vehiclePlate = '') => {
    try {
      const response = await axios.post(
        `${API_URL}/api/auth/register`,
        {
          email,
          password,
          name,
          phone,
          role,
          ...(role === 'chauffeur' ? { vehicle_model: vehicleModel, vehicle_plate: vehiclePlate } : {}),
        },
        { withCredentials: true }
      );
      return response.data;
    } catch (error) {
      throw buildClientError(error, "Erreur lors de l'inscription");
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API_URL}/api/auth/logout`, {}, { withCredentials: true });
    } finally {
      setUser(null);
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
